/**
 * <load-more-button> — a manual "Show more products" control for collection
 * and search grids. Fetches the next paginated page's HTML, pulls out the
 * product card items, and appends them to the current grid, then updates
 * itself to point at the following page (or removes itself once there are
 * no more pages).
 *
 * Deliberately self-contained (no @theme/* imports) so it doesn't depend on
 * the private internals of the auto-infinite-scroll components.
 */
class LoadMoreButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
  }

  handleClick = () => {
    this.loadNextPage();
  };

  async loadNextPage() {
    const url = this.dataset.nextUrl;
    const sectionId = this.dataset.sectionId;
    if (!url || !this.button) return;

    this.button.setAttribute('disabled', 'true');
    this.button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const sectionSelector = sectionId ? `#shopify-section-${sectionId} ` : '';
      const newGrid = doc.querySelector(`${sectionSelector}[ref="grid"]`);
      const currentGrid = document.querySelector(`${sectionSelector}[ref="grid"]`);

      if (newGrid && currentGrid) {
        const newItems = newGrid.querySelectorAll(':scope > [ref="cards[]"]');
        currentGrid.append(...newItems);
      }

      const newLoadMore = doc.querySelector('load-more-button');
      const nextUrl = newLoadMore?.dataset.nextUrl;

      if (nextUrl) {
        this.dataset.nextUrl = nextUrl;
      } else {
        this.remove();
      }

      history.pushState('', '', url);
    } catch (error) {
      // Leave the button enabled so the shopper can retry.
      console.error('Failed to load more products', error);
    } finally {
      if (this.isConnected) {
        this.button?.removeAttribute('disabled');
        this.button?.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('load-more-button')) {
  customElements.define('load-more-button', LoadMoreButton);
}
